<template>
  <div
    class="editor-container"
    :class="{ 'editor-mode': !readOnly }"
    ref="editor"
  ></div>
</template>

<script>
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import Paragraph from '@editorjs/paragraph';
import Quote from '@editorjs/quote';
import Image from '@editorjs/image';
import Embed from '@editorjs/embed';
import Table from '@editorjs/table';
import Code from '@editorjs/code';
import LinkTool from '@editorjs/link';
import RawTool from '@editorjs/raw';
import NestedList from '@editorjs/nested-list';
import AttachesTool from '@editorjs/attaches';
import directus from '../../composables/useDirectus.js';
const { VITE_apiUrl: apiUrl } = import.meta.env;

export default {
  props: {
    value: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    this.initializeEditor();
  },
  data() {
    return {
      editor: null
    };
  },
  methods: {
    initializeEditor() {
      const modifiedData = this.replaceAssetsUrl(this.value);

      const editorConfig = {
        holder: this.$refs.editor,
        autofocus: false,
        readOnly: this.readOnly,
        data: modifiedData,
        logLevel: 'ERROR',
        minHeight: 0,
        tools: {
          header: {
            class: Header,
            inlineToolbar: true
          },
          paragraph: {
            class: Paragraph,
            inlineToolbar: true
          },
          attaches: {
            class: AttachesTool,
            inlineToolbar: true,
            config: {
              uploader: {
                uploadByFile: (file) => this.uploadFileByFile(file)
              }
            }
          },
          nestedlist: {
            class: NestedList,
            inlineToolbar: true
          },
          quote: {
            class: Quote,
            inlineToolbar: true
          },
          image: {
            class: Image,
            inlineToolbar: true,
            config: {
              uploader: {
                uploadByFile: (file) => this.uploadImageByFile(file),
                uploadByUrl: (url) => this.uploadImageByUrl(url)
              }
            }
          },
          embed: {
            class: Embed,
            inlineToolbar: true
          },
          table: {
            class: Table,
            inlineToolbar: true
          },
          code: {
            class: Code,
            inlineToolbar: true
          },
          linkTool: {
            class: LinkTool,
            inlineToolbar: true
          },
          raw: {
            class: RawTool,
            inlineToolbar: true
          }
        }
      };

      this.editor = new EditorJS(editorConfig);

      this.editor.isReady
      .then(() => {
        this.postProcessEditorContent();
      })
      .catch(e => {
        console.error('Editor.js initialization error', e);
      });
    },
    postProcessEditorContent() {
      const editorElement = this.$refs.editor;
      if (!editorElement) return;
      
      const links = editorElement.querySelectorAll('a');
      links.forEach(link => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      });
    },
    replaceAssetsUrl(obj) {
      if (typeof obj !== 'object' || obj === null) return obj;
      if (Array.isArray(obj)) return obj.map(this.replaceAssetsUrl);
      return Object.keys(obj).reduce((acc, key) => {
        let val = obj[key];
        if (typeof val === 'string' && val.startsWith('/assets/')) {
          val = apiUrl+'/assets/' + val.slice(8);
        } else {
          val = this.replaceAssetsUrl(val);
        }
        acc[key] = val;
        return acc;
      }, {});
    },

    async uploadImageByFile(file) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        // Upload using Directus SDK
        const result = await directus.files.createOne(formData);

        return {
          success: 1,
          file: {
            url: apiUrl + '/assets/' + result.id
          }
        };
      } catch (error) {
        console.error('Image upload error:', error);
        return {
          success: 0,
          file: {
            url: ''
          }
        };
      }
    },

    async uploadImageByUrl(url) {
      return {
        success: 1,
        file: {
          url: url
        }
      };
    },

    async uploadFileByFile(file) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        // Upload using Directus SDK
        const result = await directus.files.createOne(formData);

        return {
          success: 1,
          file: {
            url: apiUrl + '/assets/' + result.id,
            size: result.filesize,
            name: result.filename_download,
            extension: result.type
          }
        };
      } catch (error) {
        console.error('File upload error:', error);
        return {
          success: 0
        };
      }
    }
  }
};
</script>

<style>
.ce-block__content {
  margin: 0;
  max-width: none;
}

.cdx-input.image-tool__caption:empty {
  display: none;
}

.image-tool__image-picture {
  border-radius: 12px;
  overflow: hidden;
}

.ce-code__textarea {
  min-height: auto !important;
}

.editor-mode .ce-toolbar__content,
.editor-mode .ce-block__content {
    max-width: unset;
    margin-right: 34px;
    margin-left: 34px;
}

/* Position toolbar on the left */
.ce-toolbar__plus {
  left: 0 !important;
  right: auto !important;
}

.ce-toolbar__actions {
  right: auto !important;
  left: -50px !important;
}

.editor-mode {
  padding: 20px;
  border: 1px solid var(--cs-gray-200);
  border-radius: var(--border-rad-md);
  background: var(--cs-white);
  transition: border-color 0.2s ease;
  min-height: 400px;
}

[data-theme="dark"] .editor-mode {
  border-color: var(--cs-gray-600);
  background: var(--cs-gray-800);
}

.editor-mode:focus-within {
  border-color: var(--cs-gray-300);
}
</style>

<style scoped>
.editor-container .codex-editor__redactor {
  padding-bottom: 0 !important;
}


</style>
